import {
  Autocomplete,
  Button,
  type Item,
  SelectInput,
  SelectInputItem,
  TextField,
} from '@ads/components-react';
import { AdjustmentsHorizontalIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';
import { useClassRoomDisciplinesStatus } from '../../../hooks/useClassRoomDisciplinesStatus';
import { useDisciplineDropdown } from '../../../hooks/useDisciplineDropdown';
import { useExamFormOptions } from '../../../hooks/useExamFormOptions';
import type { ExamFilters as ExamFiltersType } from '../../../types/exam';

export interface ExamFiltersProps {
  readonly filters: ExamFiltersType;
  readonly onFiltersChange: (partial: Partial<ExamFiltersType>) => void;
  readonly onApply: (partial: Partial<ExamFiltersType>) => void;
  readonly onClear: () => void;
}

/**
 * Painel de filtros da listagem de avaliações.
 *
 * Filtros primários (sempre visíveis): busca por título, tipo e status.
 * Filtros avançados (colapsáveis via "Mais filtros"): disciplina e status do planejamento.
 * Todos os filtros são enviados server-side via `useExamList`.
 *
 * @param filters        - Estado atual dos filtros (do átomo)
 * @param onFiltersChange - Atualiza um filtro sem disparar nova request (dropdowns)
 * @param onApply        - Aplica filtros e incrementa searchTrigger (botão Pesquisar)
 * @param onClear        - Limpa todos os filtros
 */
export function ExamFilters({ filters, onFiltersChange, onApply, onClear }: ExamFiltersProps) {
  const { t } = useTranslation('examManagement');
  const { t: tCommon } = useTranslation();

  const { options: classRoomStatusOptions } = useClassRoomDisciplinesStatus();
  const [disciplineSearch, setDisciplineSearch] = useState('');
  const { disciplines, isQuerySuccess: isDisciplineQuerySuccess } =
    useDisciplineDropdown(disciplineSearch);
  const { examTypes } = useExamFormOptions();

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.search);

  const disciplineItems: Item<string>[] = disciplines.map(d => ({
    label: d.value ?? '',
    value: String(d.key ?? ''),
  }));

  const selectedDiscipline = disciplineItems.find(
    d => d.value === filters.discipline && filters.discipline !== 'all',
  );

  const disciplineMessage = getAutocompleteSearchMessage(
    disciplineSearch.length,
    isDisciplineQuerySuccess,
    disciplineItems.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  const hasAnyActiveFilter =
    !!filters.search ||
    filters.type !== 'all' ||
    filters.status !== 'all' ||
    filters.discipline !== 'all' ||
    filters.classRoomDisciplineStatus !== 'all';

  const handleSearch = () => {
    onApply({ search: searchValue });
  };

  const handleClear = () => {
    setSearchValue('');
    onClear();
  };

  return (
    <div className="flex flex-col gap-micro">
      {/* ── Filtros primários ───────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end gap-mini">
        {/* Busca por título */}
        <div className="min-w-[220px] flex-[2]">
          <TextField
            label={t('filters.search')}
            placeholder={t('filters.searchPlaceholder')}
            value={searchValue}
            fullWidth
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') handleSearch();
            }}
            size="md"
          />
        </div>

        {/* Tipo de avaliação — usa chaves numéricas da API */}
        <div className="min-w-[160px] flex-1">
          <SelectInput
            label={t('filters.type')}
            value={filters.type}
            onValueChange={v => onFiltersChange({ type: v })}
            size="md"
            fullWidth
          >
            <SelectInputItem value="all" custom={{ root: 'text-nowrap' }}>
              {t('filters.allTypes')}
            </SelectInputItem>
            {examTypes.map(et => (
              <SelectInputItem
                key={et.key}
                value={String(et.key ?? '')}
                custom={{ root: 'text-nowrap' }}
              >
                {et.value ?? ''}
              </SelectInputItem>
            ))}
          </SelectInput>
        </div>

        {/* Status ativo/inativo */}
        <div className="min-w-[130px] flex-1">
          <SelectInput
            label={t('filters.status')}
            value={filters.status}
            onValueChange={v => onFiltersChange({ status: v })}
            size="md"
            fullWidth
          >
            <SelectInputItem value="all" custom={{ root: 'text-nowrap' }}>
              {t('filters.allStatus')}
            </SelectInputItem>
            <SelectInputItem value="active" custom={{ root: 'text-nowrap' }}>
              {t('filters.active')}
            </SelectInputItem>
            <SelectInputItem value="inactive" custom={{ root: 'text-nowrap' }}>
              {t('filters.inactive')}
            </SelectInputItem>
          </SelectInput>
        </div>

        {/* Botão Mais / Menos filtros */}
        <div className="flex shrink-0 items-end">
          <Button
            type="button"
            onClick={() => setShowAdvanced(prev => !prev)}
            leadingIcon={showAdvanced ? ChevronUpIcon : AdjustmentsHorizontalIcon}
            hierarchy="secondary"
          >
            {showAdvanced ? t('filters.lessFilters') : t('filters.moreFilters')}
          </Button>
        </div>
      </div>

      {/* ── Filtros avançados (colapsáveis) ────────────────────────────────── */}
      {showAdvanced && (
        <div className="flex flex-wrap items-end gap-nano rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-mini">
          <p className="w-full ts-caption-xxs text-ctx-content-base">
            {t('filters.advancedFilters')}
          </p>

          {/* Disciplina */}
          <div className="min-w-[180px] flex-1">
            <Autocomplete<string>
              label={t('filters.discipline')}
              placeholder={t('filters.disciplinePlaceholder')}
              list={disciplineItems}
              custom={{ optionList: '' }}
              {...(disciplineMessage !== undefined ? { notFoundMessage: disciplineMessage } : {})}
              {...(selectedDiscipline !== undefined ? { selectedItem: selectedDiscipline } : {})}
              handleChange={item => {
                onFiltersChange({ discipline: item.value || 'all' });
                setDisciplineSearch('');
              }}
              onChange={e => setDisciplineSearch(e.target.value)}
              handleClearQuery={() => {
                setDisciplineSearch('');
                onFiltersChange({ discipline: 'all' });
              }}
              size="md"
            />
          </div>

          {/* Status do planejamento */}
          {classRoomStatusOptions.length > 0 && (
            <div className="min-w-[180px] flex-1">
              <SelectInput
                label={t('filters.classRoomDisciplineStatus')}
                value={filters.classRoomDisciplineStatus}
                onValueChange={v => onFiltersChange({ classRoomDisciplineStatus: v })}
                size="md"
                fullWidth
              >
                <SelectInputItem value="all" custom={{ root: 'text-nowrap' }}>
                  {t('filters.allStatus')}
                </SelectInputItem>
                {classRoomStatusOptions.map(item => (
                  <SelectInputItem
                    key={item.key}
                    value={String(item.key ?? '')}
                    custom={{ root: 'text-nowrap' }}
                  >
                    {item.value ?? ''}
                  </SelectInputItem>
                ))}
              </SelectInput>
            </div>
          )}
        </div>
      )}

      {/* ── Ações ──────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-nano">
        <Button hierarchy="primary" size="md" onClick={handleSearch}>
          {t('filters.searchButton')}
        </Button>
        {hasAnyActiveFilter && (
          <Button hierarchy="secondary" size="md" onClick={handleClear}>
            {t('filters.clear')}
          </Button>
        )}
      </div>
    </div>
  );
}
